import React, { useEffect, useState, useMemo } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faBan, faCheck, faUsers, faWallet,
  faChevronRight, faSearch, faRotate, faUserShield,
  faCoins, faBuilding, faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { swalConfirm } from '../../components/shared/swalConfirm';
import DataTable, { Column } from '../../components/shared/DataTable';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
  UiModal,
  UiButton,
} from '../../components/ui';
import type { StatCardDef } from '../../components/ui';
import dayjs from 'dayjs';

interface Reseller {
  id: string;
  email: string;
  name: string;
  company_name: string;
  phone: string;
  fee_rate: number;
  payout_fee_rate: number;
  p2p_fee_rate: number | null;
  p2p_payout_fee_rate: number | null;
  is_active: boolean;
  created_at: string;
  parent_reseller_id: string | null;
  payin_spread?: number;
  payout_spread?: number;
  wallet: { balance: number; total_credit: number; total_debit: number } | null;
  merchant_count: number;
}

const fmt = (n: number) =>
  new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2 }).format(n);

const emptyForm = {
  email: '', password: '', name: '', company_name: '',
  phone: '', fee_rate: '', payout_fee_rate: '', p2p_fee_rate: '', p2p_payout_fee_rate: '', parent_reseller_id: '',
};

const INPUT_CLASS =
  'w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';
const LABEL_CLASS =
  'block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5';
const TABLE_ACTION_BTN =
  'w-8 h-8 rounded-lg border transition-colors flex items-center justify-center';

const ResellersManagementPage: React.FC = () => {
  const [resellers, setResellers] = useState<Reseller[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingReseller, setEditingReseller] = useState<Reseller | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { loadResellers(); }, []);

  const loadResellers = async () => {
    try {
      setLoading(true);
      const res = await merchantApi.get('/admin/resellers');
      setResellers(res.data.data || []);
    } catch {
      toast.error('Error loading resellers');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await merchantApi.post('/admin/resellers', {
        ...form,
        fee_rate: parseFloat(form.fee_rate) || 0,
        payout_fee_rate: parseFloat(form.payout_fee_rate) || 0,
        p2p_fee_rate: form.p2p_fee_rate === '' ? null : parseFloat(form.p2p_fee_rate),
        p2p_payout_fee_rate: form.p2p_payout_fee_rate === '' ? null : parseFloat(form.p2p_payout_fee_rate),
        parent_reseller_id: form.parent_reseller_id || null,
      });
      toast.success('Created reseller successfully!');
      closeModal();
      setForm(emptyForm);
      loadResellers();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error creating reseller');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingReseller) return;
    setSubmitting(true);
    try {
      const updateData: Record<string, unknown> = {
        name: form.name,
        company_name: form.company_name,
        phone: form.phone,
        fee_rate: parseFloat(form.fee_rate) || 0,
        payout_fee_rate: parseFloat(form.payout_fee_rate) || 0,
        p2p_fee_rate: form.p2p_fee_rate === '' ? null : parseFloat(form.p2p_fee_rate),
        p2p_payout_fee_rate: form.p2p_payout_fee_rate === '' ? null : parseFloat(form.p2p_payout_fee_rate),
      };
      if (form.password) updateData.password = form.password;
      await merchantApi.put(`/admin/resellers/${editingReseller.id}`, updateData);
      toast.success('Updated successfully!');
      closeModal();
      setForm(emptyForm);
      loadResellers();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error updating reseller');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (r: Reseller) => {
    const ok = await swalConfirm({
      title: `ระงับ "${r.name}"?`,
      text: 'Reseller จะเข้าสู่ระบบไม่ได้จนกว่าจะเปิดใช้งานอีกครั้ง',
      icon: 'warning',
      confirmText: 'ระงับ',
      confirmColor: '#ef4444',
    });
    if (!ok) return;
    try {
      await merchantApi.post(`/admin/resellers/${r.id}/deactivate`);
      toast.success('Reseller deactivated');
      loadResellers();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error');
    }
  };

  const handleActivate = async (r: Reseller) => {
    try {
      await merchantApi.put(`/admin/resellers/${r.id}`, { is_active: true });
      toast.success('Reseller activated');
      loadResellers();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error');
    }
  };

  const openEditModal = (r: Reseller) => {
    setEditingReseller(r);
    setForm({
      email: r.email, password: '', name: r.name,
      company_name: r.company_name || '', phone: r.phone || '',
      fee_rate: String(r.fee_rate),
      payout_fee_rate: String(r.payout_fee_rate || 0),
      p2p_fee_rate: r.p2p_fee_rate != null ? String(r.p2p_fee_rate) : '',
      p2p_payout_fee_rate: r.p2p_payout_fee_rate != null ? String(r.p2p_payout_fee_rate) : '',
      parent_reseller_id: r.parent_reseller_id || '',
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingReseller(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const closeModal = () => {
    if (submitting) return;
    setShowModal(false);
    setEditingReseller(null);
  };

  const topLevel = resellers.filter(r => !r.parent_reseller_id);
  const hierarchical: Reseller[] = [];
  topLevel.forEach(p => {
    hierarchical.push(p);
    resellers.filter(r => r.parent_reseller_id === p.id).forEach(s => hierarchical.push(s));
  });
  resellers.filter(r => r.parent_reseller_id && !hierarchical.find(x => x.id === r.id))
    .forEach(s => hierarchical.push(s));

  const filtered = hierarchical.filter(r =>
    !search ||
    r.name.toLowerCase().includes(search.toLowerCase()) ||
    r.email.toLowerCase().includes(search.toLowerCase()) ||
    (r.company_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const totalActive = resellers.filter(r => r.is_active).length;
  const totalSub = resellers.filter(r => r.parent_reseller_id).length;
  const totalWallet = resellers.reduce((s, r) => s + (r.wallet?.balance || 0), 0);
  const totalComm = resellers.reduce((s, r) => s + (r.wallet?.total_credit || 0), 0);

  const statCards: StatCardDef[] = useMemo(() => [
    {
      icon: faUserShield,
      iconClassName: 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300',
      label: 'ทั้งหมด',
      value: <span className="text-gray-800 dark:text-slate-100">{resellers.length} คน</span>,
    },
    {
      icon: faCheck,
      iconClassName: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      label: 'Active',
      value: <span className="text-green-600 dark:text-green-400">{totalActive} คน</span>,
    },
    {
      icon: faBuilding,
      iconClassName: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
      label: 'Top-level',
      value: <span className="text-blue-600 dark:text-blue-400">{topLevel.length} คน</span>,
    },
    {
      icon: faChevronRight,
      iconClassName: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
      label: 'Sub',
      value: <span className="text-orange-600 dark:text-orange-400">{totalSub} คน</span>,
    },
    {
      icon: faWallet,
      iconClassName: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
      label: 'Wallet รวม',
      value: <span className="text-emerald-600 dark:text-emerald-400">฿{fmt(totalWallet)}</span>,
    },
    {
      icon: faCoins,
      iconClassName: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
      label: 'Commission รวม',
      value: <span className="text-amber-600 dark:text-amber-400">฿{fmt(totalComm)}</span>,
    },
  ], [resellers.length, totalActive, topLevel.length, totalSub, totalWallet, totalComm]);

  const columns: Column<Reseller>[] = useMemo(() => [
    {
      key: 'name',
      header: 'Reseller',
      render: (r) => {
        const isSub = !!r.parent_reseller_id;
        const initials = r.name.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2);
        const parentName = isSub ? resellers.find(p => p.id === r.parent_reseller_id)?.name : null;
        return (
          <div className={`flex items-center gap-3 ${isSub ? 'pl-4' : ''}`}>
            {isSub && (
              <div className="flex flex-col items-center self-stretch mr-1">
                <div className="w-px flex-1 bg-orange-200 dark:bg-orange-800" />
                <FontAwesomeIcon icon={faChevronRight} className="text-orange-300 dark:text-orange-600 text-[9px] my-0.5" />
                <div className="w-px flex-1 bg-transparent" />
              </div>
            )}
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold text-white flex-shrink-0 shadow-sm ${
              isSub ? 'bg-gradient-to-br from-emerald-400 to-teal-500' : 'bg-gradient-to-br from-orange-400 to-amber-500'
            }`}>
              {initials}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-semibold text-gray-800 dark:text-slate-100 text-sm leading-tight">{r.name}</p>
                {isSub && (
                  <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
                    Sub
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-400 dark:text-slate-500 leading-tight">{r.email}</p>
              {r.company_name && <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5">{r.company_name}</p>}
              {isSub && parentName && (
                <p className="text-[10px] text-orange-400 dark:text-orange-400 mt-0.5">↳ {parentName}</p>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'fee_rate',
      header: 'Rate (P.In / P.Out)',
      render: (r) => {
        const isSub = !!r.parent_reseller_id;
        return (
          <div className="inline-flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">
                {r.fee_rate}%
              </span>
              <span className="text-[9px] text-gray-400 dark:text-slate-500">P.In</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300">
                {r.payout_fee_rate || 0}%
              </span>
              <span className="text-[9px] text-gray-400 dark:text-slate-500">P.Out</span>
            </div>
            {(r.p2p_fee_rate != null || r.p2p_payout_fee_rate != null) && (
              <div className="flex items-center gap-1.5">
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300">
                  {r.p2p_fee_rate != null ? `${r.p2p_fee_rate}%` : '-'} / {r.p2p_payout_fee_rate != null ? `${r.p2p_payout_fee_rate}%` : '-'}
                </span>
                <span className="text-[9px] text-gray-400 dark:text-slate-500">P2P</span>
              </div>
            )}
            {isSub && r.payin_spread !== undefined && (
              <div className="flex items-center gap-1 mt-0.5">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                  r.payin_spread > 0 ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-slate-500'
                }`}>
                  Δ{r.payin_spread}%
                </span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                  (r.payout_spread || 0) > 0 ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400' : 'bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-slate-500'
                }`}>
                  Δ{r.payout_spread || 0}%
                </span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'merchant_count',
      header: 'Merchants',
      render: (r) => (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-800">
          <FontAwesomeIcon icon={faUsers} className="text-[10px]" />
          {r.merchant_count}
        </span>
      ),
    },
    {
      key: 'wallet',
      header: 'Wallet Balance',
      render: (r) => (
        <div>
          <div className="flex items-center gap-1.5 text-sm font-semibold text-emerald-600 dark:text-emerald-400">
            <FontAwesomeIcon icon={faWallet} className="text-xs text-emerald-400 dark:text-emerald-500" />
            ฿{fmt(r.wallet?.balance || 0)}
          </div>
          <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5">
            ถอน ฿{fmt(r.wallet?.total_debit || 0)}
          </p>
        </div>
      ),
    },
    {
      key: 'total_credit',
      header: 'Total Commission',
      render: (r) => (
        <p className="text-sm font-bold text-amber-600 dark:text-amber-400">฿{fmt(r.wallet?.total_credit || 0)}</p>
      ),
    },
    {
      key: 'created_at',
      header: 'สมัครเมื่อ',
      render: (r) => (
        <p className="text-xs text-gray-600 dark:text-slate-300 font-mono text-center">{dayjs(r.created_at).format('DD/MM/YY')}</p>
      ),
    },
    {
      key: 'is_active',
      header: 'สถานะ',
      render: (r) => r.is_active ? (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
          <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
          Active
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400">
          <span className="w-1.5 h-1.5 bg-gray-400 dark:bg-slate-500 rounded-full" />
          Inactive
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (r) => (
        <div className="flex items-center justify-center gap-1.5">
          <button
            type="button"
            onClick={() => openEditModal(r)}
            className={`${TABLE_ACTION_BTN} text-blue-500 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 border-blue-100 dark:border-blue-800`}
            title="Edit"
          >
            <FontAwesomeIcon icon={faEdit} className="text-sm" />
          </button>
          {r.is_active ? (
            <button
              type="button"
              onClick={() => handleDeactivate(r)}
              className={`${TABLE_ACTION_BTN} text-red-400 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 border-red-100 dark:border-red-800`}
              title="Deactivate"
            >
              <FontAwesomeIcon icon={faBan} className="text-sm" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => handleActivate(r)}
              className={`${TABLE_ACTION_BTN} text-emerald-500 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 border-emerald-100 dark:border-emerald-800`}
              title="Activate"
            >
              <FontAwesomeIcon icon={faCheck} className="text-sm" />
            </button>
          )}
        </div>
      ),
    },
  ], [resellers]);

  const handleFilterReset = () => setSearch('');

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faUserShield}
        title="Reseller Management"
        subtitle={`จัดการ Resellers และ Fee Rates • ${resellers.length.toLocaleString()} รายการ`}
        actions={
          <UiButton icon={faPlus} onClick={openCreateModal}>
            New Reseller
          </UiButton>
        }
      />

      <UiStatsGrid cards={statCards} loading={loading} cols={3} />

      <UiFilterPanel onSearch={() => {}} onReset={handleFilterReset} loading={loading} actions={
        <button
          type="button"
          onClick={() => loadResellers()}
          disabled={loading}
          className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-50 transition-colors text-gray-600 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-slate-300 flex items-center gap-2 disabled:opacity-50"
        >
          <FontAwesomeIcon icon={faRotate} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      }>
        <div className="w-[280px] shrink-0">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
            <FontAwesomeIcon icon={faSearch} className="mr-1" />
            ค้นหา
          </label>
          <div className="relative">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 text-sm" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="ค้นหาชื่อ, email..."
              className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-400 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:placeholder-slate-500 dark:focus:ring-slate-600 dark:focus:border-slate-500"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300"
              >
                <FontAwesomeIcon icon={faXmark} className="text-sm" />
              </button>
            )}
          </div>
        </div>
      </UiFilterPanel>

      <DataTable
        data={filtered}
        columns={columns}
        loading={loading}
        keyExtractor={(r) => r.id}
        emptyMessage={search ? 'ไม่พบ Reseller ที่ค้นหา' : 'ไม่พบ Reseller'}
        emptyIcon={<FontAwesomeIcon icon={faUserShield} className="text-5xl text-gray-200 dark:text-slate-600" />}
        rowClassName={(r) => r.parent_reseller_id ? 'bg-orange-50/20 dark:bg-orange-900/10' : ''}
        hoverable
        compact
        pagination={{
          enabled: true,
          pageSize: 20,
          pageSizeOptions: [10, 20, 50],
          showPageSizeSelector: true,
          showInfo: true,
        }}
      />

      {filtered.length > 0 && (
        <div className="mt-3 px-4 py-3 bg-gray-50/60 dark:bg-slate-800/60 rounded-xl border border-gray-100 dark:border-slate-700 flex flex-wrap items-center gap-4 text-xs text-gray-500 dark:text-slate-400">
          <span className="font-medium text-gray-600 dark:text-slate-300">{filtered.length} รายการ</span>
          <span className="w-px h-4 bg-gray-200 dark:bg-slate-600" />
          <span>Active: <strong className="text-green-600 dark:text-green-400">{filtered.filter(r => r.is_active).length}</strong></span>
          <span>Sub: <strong className="text-orange-500 dark:text-orange-400">{filtered.filter(r => r.parent_reseller_id).length}</strong></span>
          <span className="w-px h-4 bg-gray-200 dark:bg-slate-600" />
          <span>Wallet รวม: <strong className="text-emerald-600 dark:text-emerald-400">฿{fmt(filtered.reduce((s, r) => s + (r.wallet?.balance || 0), 0))}</strong></span>
          <span>Commission รวม: <strong className="text-amber-600 dark:text-amber-400">฿{fmt(filtered.reduce((s, r) => s + (r.wallet?.total_credit || 0), 0))}</strong></span>
        </div>
      )}

      <UiModal
        open={showModal}
        onClose={closeModal}
        title={editingReseller ? `แก้ไข: ${editingReseller.name}` : 'สร้าง Reseller ใหม่'}
        icon={editingReseller ? faEdit : faPlus}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="md"
        scrollable
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={closeModal} disabled={submitting} className="flex-1 justify-center">
              Cancel
            </UiButton>
            <UiButton
              onClick={() => (document.getElementById('reseller-form') as HTMLFormElement | null)?.requestSubmit()}
              loading={submitting}
              className="flex-1 justify-center"
            >
              {editingReseller ? 'Update' : 'Create'}
            </UiButton>
          </div>
        }
      >
        <form id="reseller-form" onSubmit={editingReseller ? handleUpdate : handleCreate} className="space-y-4">
          {!editingReseller && (
            <div>
              <label className={LABEL_CLASS}>Email *</label>
              <input type="email" value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })} required className={INPUT_CLASS} />
            </div>
          )}

          <div>
            <label className={LABEL_CLASS}>
              Password {editingReseller ? <span className="text-gray-400 dark:text-slate-500 font-normal">(เว้นว่างถ้าไม่เปลี่ยน)</span> : '*'}
            </label>
            <input type="password" value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              required={!editingReseller} className={INPUT_CLASS} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL_CLASS}>Name *</label>
              <input type="text" value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })} required className={INPUT_CLASS} />
            </div>
            <div>
              <label className={LABEL_CLASS}>Phone</label>
              <input type="text" value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })} className={INPUT_CLASS} />
            </div>
          </div>

          <div>
            <label className={LABEL_CLASS}>Company Name</label>
            <input type="text" value={form.company_name}
              onChange={e => setForm({ ...form, company_name: e.target.value })} className={INPUT_CLASS} />
          </div>

          {!editingReseller && (
            <div>
              <label className={LABEL_CLASS}>Parent Reseller</label>
              <select value={form.parent_reseller_id}
                onChange={e => setForm({ ...form, parent_reseller_id: e.target.value })}
                className={INPUT_CLASS}>
                <option value="">— Top-level reseller —</option>
                {topLevel.map(p => (
                  <option key={p.id} value={p.id}>{p.name} ({p.fee_rate}% / {p.payout_fee_rate}%)</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">เลือกถ้าต้องการให้เป็น Sub-Reseller</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL_CLASS}>Payin Fee Rate (%)</label>
              <input type="number" step="0.01" min="0" value={form.fee_rate}
                onChange={e => setForm({ ...form, fee_rate: e.target.value })} required
                placeholder="e.g. 2.00" className={INPUT_CLASS} />
            </div>
            <div>
              <label className={LABEL_CLASS}>Payout Fee Rate (%)</label>
              <input type="number" step="0.01" min="0" value={form.payout_fee_rate}
                onChange={e => setForm({ ...form, payout_fee_rate: e.target.value })}
                placeholder="e.g. 1.00" className={INPUT_CLASS} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL_CLASS}>P2P Payin Rate (%)</label>
              <input type="number" step="0.01" min="0" max="100" value={form.p2p_fee_rate}
                onChange={e => setForm({ ...form, p2p_fee_rate: e.target.value })}
                placeholder="เว้นว่าง = ใช้ rate ปกติ" className={INPUT_CLASS} />
            </div>
            <div>
              <label className={LABEL_CLASS}>P2P Payout Rate (%)</label>
              <input type="number" step="0.01" min="0" max="100" value={form.p2p_payout_fee_rate}
                onChange={e => setForm({ ...form, p2p_payout_fee_rate: e.target.value })}
                placeholder="เว้นว่าง = ใช้ rate ปกติ" className={INPUT_CLASS} />
            </div>
          </div>
          <p className="text-xs text-gray-400 dark:text-slate-500 -mt-1">ต้นทุน P2P ของ Reseller — ใช้คิดค่าคอมเฉพาะรายการ P2P (เว้นว่าง = ใช้ rate ปกติ ซึ่งมักสูงกว่า = ไม่ได้ค่าคอม P2P)</p>
        </form>
      </UiModal>
    </div>
  );
};

export default ResellersManagementPage;
